/**
 * Полоса очереди записей в Bitrix над полями сделки (лида, формы новой сделки): Bitrix не ответил —
 * «Отправим в Bitrix … · повтор через N с», дошло — «Дослано» и блок перечитывается, не дошло за
 * 10 минут или в Bitrix уже поменяли — красным с «Повторить» / «Отменить».
 * Опрос раз в 5 с — только пока есть ждущие команды. Спека `docs/specs/2026-10-22-bitrix-outbox-design.md`.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  type BitrixCommand,
  type CommandTarget,
  cancelCommand,
  isWaiting,
  loadCommands,
  retryCommand,
} from "@/lib/bitrixCommands";

/** ««Чаты»: стадия Думают — Орлова Нина» → «стадия Думают»: экран и автор человеку здесь не нужны. */
const short = (what: string) => what.replace(/^«[^»]+»:\s*/, "").replace(/\s—\s[^—]+$/, "");

const seconds = (iso: string | null, now: number) =>
  iso ? Math.max(0, Math.round((Date.parse(iso) - now) / 1000)) : 0;

export function BitrixPending({
  deal,
  lead,
  chat,
  refreshKey,
  onDone,
}: CommandTarget & {
  /** Сменился — перечитать очередь (карточка сделки перечитана после правки). */
  refreshKey?: number;
  /** Команда дошла после сбоя или снята — перечитать то, что показывает блок. */
  onDone?: () => void;
}) {
  const target = { deal, lead, chat };
  const enabled = Boolean(deal || lead || chat);
  const q = useQuery({
    queryKey: ["bitrix-commands", deal ?? null, lead ?? null, chat ?? null],
    queryFn: () => loadCommands(target),
    enabled,
    refetchInterval: (query) => (query.state.data?.some(isWaiting) ? 5000 : false),
  });
  const list = q.data ?? [];
  const waiting = list.some(isWaiting);

  // Правка ушла в очередь — карточку перечитали: перечитать и очередь.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (enabled) void q.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Ждала — дошла: блок показывает старое, перечитать.
  const was = useRef(new Set<number>());
  useEffect(() => {
    const done = list.some((c) => !isWaiting(c) && was.current.has(c.id));
    was.current = new Set(list.filter(isWaiting).map((c) => c.id));
    if (done) onDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  // «Повтор через N с» тикает, пока есть ждущие.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waiting]);

  const act = useMutation({
    mutationFn: ({ id, how }: { id: number; how: "retry" | "cancel" }) =>
      how === "retry" ? retryCommand(id) : cancelCommand(id),
    onSettled: () => {
      void q.refetch();
      onDone?.();
    },
  });

  if (!list.length) return null;
  return (
    <div className="flex flex-col gap-1" data-annot="очередь Bitrix">
      {list.map((c) => (
        <Row key={c.id} c={c} now={now} busy={act.isPending} onAct={(how) => act.mutate({ id: c.id, how })} />
      ))}
      {act.isError && <div className="text-[12px] text-err">{act.error.message}</div>}
    </div>
  );
}

function Row({
  c,
  now,
  busy,
  onAct,
}: {
  c: BitrixCommand;
  now: number;
  busy: boolean;
  onAct: (how: "retry" | "cancel") => void;
}) {
  const what = short(c.what);
  if (isWaiting(c)) {
    const left = seconds(c.next_at, now);
    return (
      <div className="rounded-[var(--radius-card)] bg-warn-bg px-2 py-1 text-[12px] text-warn" role="status">
        <span className="font-medium">Отправим в Bitrix:</span> {what}
        <span className="text-fg-muted">
          {" · "}
          {c.state === "sending" || left === 0 ? "отправляем…" : `повтор через ${left} с`}
          {c.attempts > 0 && ` · попытка ${c.attempts + 1}`}
        </span>
      </div>
    );
  }
  if (c.state === "done") {
    return (
      <div className="rounded-[var(--radius-card)] bg-ok-bg px-2 py-1 text-[12px] text-ok" role="status">
        <span className="font-medium">Дослано в Bitrix:</span> {what}
      </div>
    );
  }
  const conflict = c.state === "conflict";
  return (
    <div className="rounded-[var(--radius-card)] bg-err-bg px-2 py-1 text-[12px] text-err" role="alert">
      <div>
        <span className="font-medium">{conflict ? "Не записано — в Bitrix уже поменяли:" : "Не дошло до Bitrix:"}</span>{" "}
        {what}
      </div>
      {c.error && <div className="text-fg-muted">{c.error}</div>}
      <div className="mt-0.5 flex gap-3">
        <button type="button" disabled={busy} onClick={() => onAct("retry")} className="font-medium underline hover:no-underline">
          {conflict ? "Записать поверх" : "Повторить"}
        </button>
        <button type="button" disabled={busy} onClick={() => onAct("cancel")} className="text-fg-muted underline hover:no-underline">
          Отменить
        </button>
      </div>
    </div>
  );
}
